import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import {
  useCreatePackagingMethod as useCreateFarmGatePackagingMethod,
  useDeletePackagingMethod as useDeleteFarmGatePackagingMethod,
  useGetWasteManagement as useGetFarmGateWasteManagement,
  useUpdatePackagingMethod as useUpdateFarmGatePackagingMethod
} from "../../../../../api/farmGate/packaging";
import {
  useCreatePackagingMethod as useCreateProcessGatePackagingMethod,
  useDeletePackagingMethod as useDeleteProcessGatePackagingMethod,
  useGetWasteManagement as useGetProcessGateWasteManagement,
  useUpdatePackagingMethod as useUpdateProcessGatePackagingMethod
} from "../../../../../api/processGate/packaging";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import { PACKAGING_LEVELS } from "../../../../../config/constants";
import { successMessage } from "../../../../../helpers/ErrorHandler";
import PackagingMachineryFields from "./components/PackagingMachineryFields";
import PackagingSectionCollapsible from "./components/PackagingSectionCollapsible";
import { PACKAGING_SAVE_STATUS } from "./packagingLevel.config";
import { PACKAGING_GATE, PackagingGate } from "./packagingLevel.types";
import { resolveWasteManagementMethod } from "./packagingValidation.utils";

interface PackagingMethodLevelProps {
  gate: PackagingGate;
  gateId: string;
  packagingData: any[];
  refetchData: (...args: any[]) => any;
  packagingId: string | null;
  savePackingNodeData: (id: string, name?: string) => void;
  packagingName?: string;
  onPackagingSave?: (packagingId: string) => void;
  lockStatus?: boolean;
}

const EMPTY_MACHINERY_VALUES = {
  packagingMethodName: "",
  packagingMachine: "",
  energy: "",
  waste: "",
  capacity: "",
  packagingLevel: PACKAGING_LEVELS[0].value,
  wasteManagementMethod: ""
};

const PackagingMethodLevel = ({
  gate,
  gateId,
  packagingData,
  refetchData,
  packagingId,
  savePackingNodeData,
  packagingName,
  onPackagingSave = () => {},
  lockStatus = false
}: PackagingMethodLevelProps) => {
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState<any>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [packagingMethodId, setPackagingMethodId] = useState<string | null>(null);
  const [accordionEnabled, setAccordionEnabled] = useState(false);

  const farmCreate = useCreateFarmGatePackagingMethod();
  const farmUpdate = useUpdateFarmGatePackagingMethod();
  const farmDelete = useDeleteFarmGatePackagingMethod();
  const farmWaste = useGetFarmGateWasteManagement();
  const processCreate = useCreateProcessGatePackagingMethod();
  const processUpdate = useUpdateProcessGatePackagingMethod();
  const processDelete = useDeleteProcessGatePackagingMethod();
  const processWaste = useGetProcessGateWasteManagement();

  const isLoading =
    gate === PACKAGING_GATE.FARM
      ? farmCreate.isLoading || farmUpdate.isLoading || farmDelete.isLoading
      : processCreate.isLoading || processUpdate.isLoading || processDelete.isLoading;

  const wasteManagementTypes = gate === PACKAGING_GATE.FARM ? farmWaste.data : processWaste.data;

  const wasteManagementOptions = useMemo(
    () => wasteManagementTypes?.filter((option: any) => String(option.value ?? "") !== "") ?? [],
    [wasteManagementTypes]
  );

  const addForm = useForm({
    mode: "all",
    defaultValues: EMPTY_MACHINERY_VALUES
  });
  const editForm = useForm({
    mode: "all",
    defaultValues: EMPTY_MACHINERY_VALUES
  });

  const {
    handleSubmit: handleSubmitAdd,
    reset: resetAdd,
    control: addControl,
    formState: { isValid: isAddValid }
  } = addForm;

  const {
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    control: editControl,
    formState: { isValid: isEditValid }
  } = editForm;

  const getRecordFormValues = (data: any) => {
    const wasteMethod = resolveWasteManagementMethod(
      wasteManagementTypes,
      data.wasteManagementMethod
    );
    return {
      packagingMethodName: data.packagingMethodName ?? "",
      packagingMachine: data.packagingMachine ?? "",
      energy: data.energy ?? "",
      waste: data.waste ?? "",
      capacity: data.capacity ?? "",
      packagingLevel: data.packagingLevel || PACKAGING_LEVELS[0].value,
      wasteManagementMethod: wasteMethod != null ? String(wasteMethod) : ""
    };
  };

  const clearEditState = () => {
    setEditingId(null);
    setPackagingMethodId(null);
    resetEdit(EMPTY_MACHINERY_VALUES);
  };

  const openEdit = (record: any) => {
    setEditingId(record._id);
    setPackagingMethodId(record._id);
    resetEdit(getRecordFormValues(record));
  };

  const handleCancelEdit = () => {
    clearEditState();
  };

  const commentResetValue = (statusMessage: string, status: string) => {
    if (status === PACKAGING_SAVE_STATUS.ADD) {
      resetAdd(EMPTY_MACHINERY_VALUES);
    } else {
      clearEditState();
    }
    refetchData();
    successMessage(statusMessage);
  };

  const addPackageMethodData = (payload: any) => {
    const onSuccess = (response: any) => {
      onPackagingSave(response.packagingId);
      commentResetValue("Package method created successfully", PACKAGING_SAVE_STATUS.ADD);
      if (packagingId) {
        refetchData(packagingId);
        return;
      }
      savePackingNodeData(response.packagingId);
    };

    if (gate === PACKAGING_GATE.FARM) {
      farmCreate.mutate(payload, { onSuccess });
      return;
    }
    processCreate.mutate(payload, { onSuccess });
  };

  const editPackageMethodData = (payload: any) => {
    const onSuccess = () => {
      onPackagingSave(packagingId || "");
      commentResetValue("Package method updated successfully", PACKAGING_SAVE_STATUS.EDIT);
    };

    if (gate === PACKAGING_GATE.FARM) {
      farmUpdate.mutate(payload, { onSuccess });
      return;
    }
    processUpdate.mutate(payload, { onSuccess });
  };

  const packageMethodInsert = (data: any, status: string) => {
    setAccordionEnabled(true);
    data.energy = Number(data.energy);
    data.waste = Number(data.waste);
    data.capacity = Number(data.capacity);
    data.wasteManagementMethod = resolveWasteManagementMethod(
      wasteManagementTypes,
      data.wasteManagementMethod
    );

    if (status === PACKAGING_SAVE_STATUS.ADD) {
      const payload =
        gate === PACKAGING_GATE.FARM
          ? { data, packagingId, farmGateId: gateId }
          : { data, packagingId, processGateId: gateId };
      addPackageMethodData(payload);
    } else {
      const payload =
        gate === PACKAGING_GATE.FARM
          ? { data, packagingMethodId, farmGateId: gateId }
          : { data, packagingMethodId, processGateId: gateId };
      editPackageMethodData(payload);
    }
  };

  const deletePackageMethodData = (data: any) => {
    const deletePayload =
      gate === PACKAGING_GATE.FARM
        ? { farmGateId: gateId, packagingId, packagingMethodId: data._id }
        : { processGateId: gateId, packagingId, packagingMethodId: data._id };

    const onSuccess = () => {
      onPackagingSave(data.packagingId);
      if (editingId === data._id) {
        clearEditState();
      }
      refetchData();
      successMessage("Package deleted successfully");
    };

    if (gate === PACKAGING_GATE.FARM) {
      farmDelete.mutate(deletePayload, { onSuccess });
      return;
    }
    processDelete.mutate(deletePayload, { onSuccess });
  };

  const handleClick = () => {
    if (!packagingName) return;
    setAccordionEnabled(!accordionEnabled);
  };

  const hasRecords = (packagingData?.length ?? 0) > 0;
  const fieldProps = {
    lockStatus,
    gate,
    wasteManagementOptions
  };

  return (
    <>
      <PackagingSectionCollapsible
        title="Packaging Machinery"
        badge={hasRecords ? `${packagingData.length} records` : undefined}
        expanded={accordionEnabled}
        disabled={!packagingName}
        onToggle={handleClick}
      >
        <div className="tw-space-y-4">
          {hasRecords && (
            <div className="tw-space-y-3">
              {packagingData.map((record, index) => {
                const isRowEditing = editingId === record._id;

                if (isRowEditing) {
                  return (
                    <Form {...editForm} key={record._id ?? index}>
                      <div className="tw-rounded-lg tw-border tw-border-border/50 tw-bg-muted/30 tw-p-4 tw-space-y-3">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                          <p className="tw-text-sm tw-font-semibold tw-text-foreground">
                            Machinery {index + 1}
                            {record?.packagingMethodName ? ` — ${record.packagingMethodName}` : ""}
                          </p>
                          {!lockStatus && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                              onClick={() => {
                                setDeleteData(record);
                                setVisibleDeleteModal(true);
                              }}
                            >
                              <Trash2 className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          )}
                        </div>

                        <PackagingMachineryFields control={editControl} {...fieldProps} />

                        <div className="tw-flex tw-justify-end tw-gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="tw-gap-1"
                            disabled={lockStatus || isLoading}
                            onClick={handleCancelEdit}
                          >
                            <X className="tw-h-3.5 tw-w-3.5" />
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="tw-gap-1"
                            disabled={!isEditValid || lockStatus || isLoading}
                            onClick={handleSubmitEdit(data =>
                              packageMethodInsert(data, PACKAGING_SAVE_STATUS.EDIT)
                            )}
                          >
                            <Check className="tw-h-3.5 tw-w-3.5" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </Form>
                  );
                }

                return (
                  <div
                    key={record._id ?? index}
                    className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-3 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50"
                  >
                    <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-flex-wrap">
                      <span className="tw-font-semibold tw-text-muted-foreground tw-w-5">
                        {index + 1}
                      </span>
                      <span className="tw-font-medium tw-text-foreground">
                        {record?.packagingMethodName ?? "—"}
                      </span>
                      <span className="tw-text-muted-foreground">
                        {record?.packagingMachine || "—"}
                      </span>
                      <span className="tw-text-muted-foreground">
                        {record?.packagingLevel ?? "—"}
                      </span>
                      <span className="tw-font-mono tw-text-muted-foreground">
                        {record?.energy ?? "—"} kWh
                      </span>
                      <span className="tw-font-mono tw-text-muted-foreground">
                        Cap: {record?.capacity ?? "—"}
                      </span>
                      <span className="tw-font-mono tw-text-muted-foreground">
                        Waste: {record?.waste ?? "—"}
                      </span>
                    </div>
                    {!lockStatus && (
                      <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="tw-h-7 tw-w-7 tw-p-0"
                          onClick={() => openEdit(record)}
                        >
                          <Pencil className="tw-h-3.5 tw-w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                          onClick={() => {
                            setDeleteData(record);
                            setVisibleDeleteModal(true);
                          }}
                        >
                          <Trash2 className="tw-h-3.5 tw-w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hasRecords && <div className="tw-border-t tw-border-dashed tw-border-border" />}

          <div className="tw-space-y-2">
            {hasRecords && (
              <p className="tw-text-xs tw-font-medium tw-text-muted-foreground">
                Add new packaging machinery
              </p>
            )}

            <Form {...addForm}>
              <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-background tw-p-4 tw-space-y-3">
                <PackagingMachineryFields control={addControl} {...fieldProps} showPlaceholders />

                <div className="tw-flex tw-justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="tw-gap-1"
                    disabled={!isAddValid || lockStatus || isLoading}
                    onClick={handleSubmitAdd(data =>
                      packageMethodInsert(data, PACKAGING_SAVE_STATUS.ADD)
                    )}
                  >
                    <Plus className="tw-h-3.5 tw-w-3.5" /> Save Record
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </PackagingSectionCollapsible>

      {visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={() => {
              setVisibleDeleteModal(false);
              setDeleteData({});
            }}
            approve={() => {
              setVisibleDeleteModal(false);
              deletePackageMethodData(deleteData);
            }}
            title="Delete packaging machinery"
            subTitle="Are you sure you want to delete this packaging machinery record?"
          />,
          document.body
        )}
    </>
  );
};

export default PackagingMethodLevel;
